import { createFileRoute, Link } from "@tanstack/react-router";
import { useActiveCompany } from "@/hooks/use-active-company";
import { PageHeader } from "@/components/app-shell";
import {
  Zap, Gauge, Activity, TrendingDown, Cable, ShieldCheck, Cog, ArrowLeftRight,
  Grid3x3, Layers, Package, Wind, Home as HomeIcon, Columns3, Rows3, Triangle,
  SquareStack, Armchair, Fence, Boxes, Mountain, Ruler, Calculator,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { specsForModule, sharedSpecs, type CalcSpec } from "@/lib/calc-specs";

export const Route = createFileRoute("/_authenticated/calculations/")({
  component: CalculationsIndex,
});

type Card = { to: string; title: string; desc: string; icon: LucideIcon };

const ELECTRICIAN_CARDS: Card[] = [
  { to: "/calculations/ohms",         title: "Ohms lov",      desc: "U = I × R",                 icon: Zap },
  { to: "/calculations/power",        title: "Effekt",         desc: "1-fase og 3-fase",         icon: Gauge },
  { to: "/calculations/current",      title: "Strøm",          desc: "I = P / (U · cosφ)",       icon: Activity },
  { to: "/calculations/voltage-drop", title: "Spenningsfall",  desc: "ΔU i volt og prosent",     icon: TrendingDown },
  { to: "/calculations/cable",        title: "Kabel",          desc: "Foreslå tverrsnitt",       icon: Cable },
  { to: "/calculations/breaker",      title: "Vern",           desc: "Ib ≤ In ≤ Iz",             icon: ShieldCheck },
  { to: "/calculations/motor",        title: "Motor",          desc: "Merkestrøm og start",       icon: Cog },
  { to: "/calculations/conversion",   title: "Omregninger",    desc: "AWG, W/kW, A/mA, V/kV",    icon: ArrowLeftRight },
];

const CARPENTER_CARDS: Card[] = [
  { to: "/calculations/carpenter/gips",              title: "Gips",             desc: "kvm → plater, skruer, sparkel", icon: Grid3x3 },
  { to: "/calculations/carpenter/kledning",          title: "Utvendig kledning", desc: "kvm → lm bord + spiker",       icon: Layers },
  { to: "/calculations/carpenter/isolasjon",         title: "Isolasjon",        desc: "kvm × tykkelse → pakker",       icon: Package },
  { to: "/calculations/carpenter/vindsperre",            title: "Vind- / dampsperre", desc: "kvm → ruller + tape",         icon: Wind },
  { to: "/calculations/carpenter/undertak",          title: "Undertak",         desc: "takflate → ruller",             icon: HomeIcon },
  { to: "/calculations/carpenter/stendere",          title: "Bindingsverk",     desc: "lm vegg + cc → stendere",       icon: Columns3 },
  { to: "/calculations/carpenter/bjelkelag",         title: "Bjelkelag",        desc: "areal + cc → bjelker",          icon: Rows3 },
  { to: "/calculations/carpenter/lekter",            title: "Lekter og sløyfer", desc: "takflate → lm",                icon: SquareStack },
  { to: "/calculations/carpenter/taksperrer",        title: "Taksperrer",       desc: "spennvidde + vinkel → lengde",  icon: Triangle },
  { to: "/calculations/carpenter/terrassebord",      title: "Terrassebord",     desc: "areal → lm + skruer",           icon: Armchair },
  { to: "/calculations/carpenter/terrasse-bjelkelag", title: "Terrasse bjelkelag", desc: "areal + cc → bjelker",       icon: Rows3 },
  { to: "/calculations/carpenter/rekkverk",          title: "Rekkverk",         desc: "lm → stolper + spiler",         icon: Fence },
  { to: "/calculations/carpenter/betong",            title: "Betong",           desc: "m³ + sekker",                   icon: Boxes },
  { to: "/calculations/carpenter/grus",              title: "Grus / pukk",      desc: "m³ + tonn",                     icon: Mountain },
];

function CalculationsIndex() {
  const { activeModuleKey: moduleKey } = useActiveCompany();
  const isCarpenter = moduleKey === "carpenter";
  const cards = isCarpenter ? CARPENTER_CARDS : ELECTRICIAN_CARDS;
  const extraSpecs = specsForModule(isCarpenter ? "carpenter" : "electrician");
  const subtitle = isCarpenter
    ? "Digital verktøykasse for tømrer­beregninger."
    : "Digital verktøykasse for elektroberegninger.";
  const advisoryText = isCarpenter
    ? "Beregningene er veiledende hjelpemidler for mengdeberegning. Konstruktive dimensjoner må prosjekteres av kvalifisert personell."
    : "Beregninger som bygger på NEK 400 eller annet regelverk er veiledende hjelpemidler, ikke en erstatning for prosjektering av kvalifisert personell.";

  return (
    <>
      <PageHeader title="Beregninger" subtitle={subtitle} />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map((c) => (
          <Link key={c.to} to={c.to} className="card p-5 transition-shadow hover:shadow-md">
            <div className="flex items-start gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-lg bg-primary/10 text-primary">
                <c.icon className="h-5 w-5" />
              </div>
              <div>
                <div className="font-semibold">{c.title}</div>
                <div className="mt-0.5 text-sm text-muted-foreground">{c.desc}</div>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {extraSpecs.length > 0 && (
        <SpecSection
          title={isCarpenter ? "Geometri, mengder og konstruksjon" : "Beregninger og kontroll"}
          specs={extraSpecs}
          icon={Ruler}
        />
      )}

      <SpecSection title="Økonomi" specs={sharedSpecs()} icon={Calculator} />


      <p className="mt-8 text-xs text-muted-foreground">{advisoryText}</p>
    </>
  );
}

function SpecSection({ title, specs, icon: Icon }: { title: string; specs: CalcSpec[]; icon: LucideIcon }) {
  if (specs.length === 0) return null;
  return (
    <>
      <h2 className="mt-10 mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {specs.map((s) => (
          <Link
            key={s.id}
            to="/calculations/verktoy/$id"
            params={{ id: s.id }}
            className="card p-5 transition-shadow hover:shadow-md"
          >
            <div className="flex items-start gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-lg bg-primary/10 text-primary">
                <Icon className="h-5 w-5" />
              </div>
              <div>
                <div className="font-semibold">{s.title}</div>
                <div className="mt-0.5 text-sm text-muted-foreground">{s.desc}</div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </>
  );
}
